import React, { useState } from 'react';
import { MdPlayArrow, MdStop, MdTimeline, MdExpandMore, MdExpandLess, MdDelete, MdEdit, MdCheck, MdClose, MdFileDownload } from 'react-icons/md';

const SIGNAL_COLOR = { BUY: 'text-green-400', SELL: 'text-red-400', FLAT: 'text-gray-400' };
const STATUS_COLOR = { filled: 'text-green-400', order_placed: 'text-yellow-400', flat: 'text-gray-400', skipped: 'text-gray-400', error: 'text-red-400', generated: 'text-blue-400' };
const STATUS_BG    = { filled: 'bg-green-900/30 text-green-400', order_placed: 'bg-yellow-900/30 text-yellow-400', flat: 'bg-gray-800 text-gray-400', skipped: 'bg-gray-800 text-gray-400', error: 'bg-red-900/30 text-red-400', generated: 'bg-blue-900/30 text-blue-400' };

const DEFAULT_CARD_CLS = 'bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4';

/**
 * Presentational strategy card. Owns only its own UI-expansion state
 * (backtest expand, inline-alloc edit, indicators open). All data arrives
 * via props; all side-effects are delegated to callback props so the same
 * card renders identically in the Strategies tab and inside the portfolio
 * deployment modal.
 *
 * Props:
 *   s                 strategy object
 *   bt                latest BacktestResult (or undefined)
 *   sigs              array of live signals
 *   indicators        live indicators data object (or undefined)
 *   indicatorsLoading bool
 *   auditLoading      bool
 *   pendingSlices     array of pending order slices for this strategy
 *   cardCls           optional className override for the outer card
 *   onToggleActive()  onDelete()  onWithdraw()  onCancelStop()
 *   onSaveAlloc(value)  onGoBacktest()  onDownloadAudit()  onCheckIndicators()
 */
const StrategyCard = ({
  s,
  bt,
  sigs = [],
  indicators,
  indicatorsLoading = false,
  auditLoading = false,
  pendingSlices = [],
  cardCls = DEFAULT_CARD_CLS,
  onToggleActive,
  onDelete,
  onWithdraw,
  onCancelStop,
  onSaveAlloc,
  onGoBacktest,
  onDownloadAudit,
  onCheckIndicators,
}) => {
  const [isExpanded, setIsExpanded]       = useState(false);
  const [editing, setEditing]             = useState(false);
  const [allocDraft, setAllocDraft]       = useState('');
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);

  const lastSig   = sigs[0] || null;
  const idleAsset = s.idle_deployment && s.idle_deployment !== 'cash' ? s.idle_deployment : null;
  const fromOpt   = s.source_opt_run_id != null;

  // RUNNING strategy: the box takes an AMOUNT to add / remove (+1000 / -500) — never a new total (wishlist #27).
  // Stopped strategy: the box sets the seed for its next deployment (no trade, no capital flow).
  const beginEdit = () => { setAllocDraft(s.is_active ? '' : String(s.allocation_usd ?? 10000)); setEditing(true); };
  const saveAlloc = () => { onSaveAlloc && onSaveAlloc(allocDraft); setEditing(false); };

  return (
    <div className={`${cardCls} transition-shadow hover:shadow-md`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="text-base font-serif font-semibold text-gray-800 dark:text-gray-200 truncate">{s.name}</h3>
            {/* Last signal status badge */}
            {lastSig && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium uppercase tabular-nums ${STATUS_BG[lastSig.status] || 'bg-gray-800 text-gray-400'}`}>
                {lastSig.status}
              </span>
            )}
            {/* Idle deployment badge */}
            {idleAsset && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-900/30 text-blue-400 font-medium">
                Idle: {idleAsset}
              </span>
            )}
            {/* Source badge — deployed from an optimization run */}
            {fromOpt && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-900/30 text-purple-400 font-medium cursor-default"
                title={`Deployed from optimization run #${s.source_opt_run_id}, combo #${s.source_combo_index}`}
              >
                Opt #{s.source_opt_run_id}
              </span>
            )}
          </div>
          {s.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{s.description}</p>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <button
            onClick={onToggleActive}
            className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded-md font-medium uppercase ${
              s.is_active
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            {s.is_active ? <><MdStop size={12} /> Active</> : <><MdPlayArrow size={12} /> Inactive</>}
          </button>
          <button
            onClick={onDelete}
            className={`p-1 transition-colors ${
              s.is_active
                ? 'text-gray-600 cursor-not-allowed opacity-40'
                : 'text-gray-400 hover:text-red-500'
            }`}
            title={s.is_active ? 'Deactivate strategy before deleting' : 'Delete strategy'}
          >
            <MdDelete size={14} />
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="text-xs space-y-1.5 mb-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Type</span>
          <span className="text-gray-700 dark:text-gray-300 font-medium">{s.strategy_class}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Symbols</span>
          <span className="text-gray-700 dark:text-gray-300">{(s.symbols || []).join(', ') || '--'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Parameters</span>
          <span className="text-gray-700 dark:text-gray-300 font-mono text-[10px] truncate max-w-[180px]">{JSON.stringify(s.parameters)}</span>
        </div>
        {/* Allocation USD — inline editable */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Capital</span>
          {editing ? (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-gray-400">{s.is_active ? '± $' : '$'}</span>
              <input
                placeholder={s.is_active ? '+1000 / -500' : ''}
                className="w-24 bg-[#0d1117] border border-brand-gold/50 rounded px-1 py-0.5 text-[11px] text-gray-200 tabular-nums outline-none"
                value={allocDraft}
                onChange={e => setAllocDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveAlloc(); if (e.key === 'Escape') setEditing(false); }}
                autoFocus
              />
              <button onClick={saveAlloc} className="text-green-400 hover:text-green-300"><MdCheck size={13} /></button>
              <button onClick={() => setEditing(false)} className="text-gray-500 hover:text-gray-300"><MdClose size={13} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-gray-700 dark:text-gray-300 tabular-nums">
                ${Number(s.allocation_usd ?? 10000).toLocaleString('en-US')}
              </span>
              <button
                onClick={beginEdit}
                className="text-gray-500 hover:text-brand-gold transition-colors"
                title={s.is_active ? 'Add / remove capital' : 'Set the seed capital'}
              >
                <MdEdit size={11} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Last Backtest */}
      {bt && (
        <>
          <button
            onClick={() => setIsExpanded(v => !v)}
            className="flex items-center justify-between w-full py-1.5 border-t border-gray-100 dark:border-gray-800"
          >
            <span className="text-[10px] uppercase tracking-wider text-gray-400">LAST BACKTEST</span>
            {isExpanded ? <MdExpandLess className="text-gray-400" size={14} /> : <MdExpandMore className="text-gray-400" size={14} />}
          </button>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div className="text-center">
              <div className="text-[10px] text-gray-400 uppercase">Return</div>
              <div className={`text-sm font-semibold tabular-nums ${bt.total_return >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                {bt.total_return >= 0 ? '+' : ''}{Number(bt.total_return).toFixed(1)}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-gray-400 uppercase">Sharpe</div>
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 tabular-nums">{Number(bt.sharpe_ratio).toFixed(2)}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-gray-400 uppercase">Trades</div>
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 tabular-nums">{bt.num_trades}</div>
            </div>
          </div>
          {isExpanded && (
            <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="text-center">
                <div className="text-[10px] text-gray-400 uppercase">Max DD</div>
                <div className="text-sm font-semibold text-red-500 tabular-nums">{Number(bt.max_drawdown).toFixed(1)}%</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] text-gray-400 uppercase">Win Rate</div>
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 tabular-nums">{Number(bt.win_rate).toFixed(1)}%</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] text-gray-400 uppercase">Profit F</div>
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 tabular-nums">{Number(bt.profit_factor).toFixed(2)}</div>
              </div>
              <div className="col-span-3 text-[10px] text-gray-400 text-center mt-1">
                {bt.symbol} | {bt.start_date} to {bt.end_date}
              </div>
            </div>
          )}
        </>
      )}

      {/* Live Signals feed — shown for active strategies */}
      {s.is_active && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Live Signals (last 30d)
          </div>
          {sigs.length === 0 ? (
            <p className="text-[10px] text-gray-500 italic">No signals yet — runs at 19:30 ET on trading days.</p>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-[10px] min-w-[480px]">
                <thead>
                  <tr className="text-gray-500 uppercase tracking-wider">
                    <th className="text-left py-0.5">Date</th>
                    <th className="text-left py-0.5">Symbol</th>
                    <th className="text-left py-0.5">Signal</th>
                    <th className="text-right py-0.5">Ref Price</th>
                    <th className="text-right py-0.5">Qty</th>
                    <th className="text-right py-0.5">Fill</th>
                    <th className="text-right py-0.5">Comm</th>
                    <th className="text-right py-0.5">Proceeds</th>
                    <th className="text-left py-0.5 pl-2">Status</th>
                    <th className="text-left py-0.5 pl-2">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                  {sigs.slice(0, 10).map(sig => (
                    <tr key={sig.id}>
                      <td className="py-0.5 text-gray-500">{sig.signal_date}</td>
                      <td className="py-0.5 font-semibold text-gray-200">{sig.symbol}</td>
                      <td className={`py-0.5 font-semibold ${SIGNAL_COLOR[sig.signal_type] || 'text-gray-400'}`}>{sig.signal_type}</td>
                      <td className="py-0.5 text-right tabular-nums text-gray-400">{sig.price_at_signal != null ? `$${sig.price_at_signal.toFixed(2)}` : '—'}</td>
                      <td className="py-0.5 text-right tabular-nums text-gray-400">{sig.position_size ?? '—'}</td>
                      <td className="py-0.5 text-right tabular-nums text-gray-300">{sig.fill_price != null ? `$${sig.fill_price.toFixed(2)}` : '—'}</td>
                      <td className="py-0.5 text-right tabular-nums text-gray-500">{sig.commission != null ? `$${sig.commission.toFixed(2)}` : '—'}</td>
                      <td className="py-0.5 text-right tabular-nums">
                        {sig.signal_type === 'SELL' && sig.net_proceeds != null
                          ? <span className="text-green-400">${sig.net_proceeds.toFixed(2)}</span>
                          : <span className="text-gray-600">—</span>}
                      </td>
                      <td className={`py-0.5 pl-2 ${STATUS_COLOR[sig.status] || 'text-gray-400'}`}>{sig.status}</td>
                      <td
                        className="py-0.5 pl-2 text-gray-500 truncate max-w-[120px] cursor-default"
                        title={sig.notes || ''}
                      >
                        {sig.notes
                          ? sig.notes.length > 28 ? sig.notes.slice(0, 28) + '…' : sig.notes
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Current Indicators panel */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
        <button
          onClick={() => {
            if (indicators && !indicatorsLoading) {
              setIndicatorsOpen(v => !v);
            } else if (!indicatorsLoading) {
              setIndicatorsOpen(true);
              onCheckIndicators && onCheckIndicators();
            }
          }}
          className="flex items-center justify-between w-full group"
        >
          <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
            <span>⚙ Indicators</span>
            {indicatorsLoading && (
              <span className="inline-block w-2.5 h-2.5 border border-gray-500 border-t-brand-gold rounded-full animate-spin" />
            )}
          </span>
          {indicators && !indicatorsLoading ? (
            indicatorsOpen
              ? <MdExpandLess className="text-gray-400" size={14} />
              : <MdExpandMore className="text-gray-400" size={14} />
          ) : !indicatorsLoading ? (
            <span className="text-[10px] text-gray-500 group-hover:text-brand-gold transition-colors">fetch →</span>
          ) : null}
        </button>

        {indicatorsOpen && indicators && !indicatorsLoading && (() => {
          const ind = indicators;
          if (ind.error) return (
            <p className="text-[10px] text-red-400 mt-1.5 italic">{ind.error}</p>
          );
          const sigClr = ind.signal === 'buy'
            ? 'bg-green-900/30 text-green-400'
            : ind.signal === 'sell'
              ? 'bg-red-900/30 text-red-400'
              : 'bg-gray-800 text-gray-400';
          const indEntries  = Object.entries(ind.indicators  || {});
          const condEntries = Object.entries(ind.conditions || {});
          return (
            <div className="mt-2 space-y-2">
              {/* Signal + price header */}
              <div className="flex items-center justify-between">
                <span className={`text-[11px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider ${sigClr}`}>
                  {(ind.signal || 'flat').toUpperCase()}
                </span>
                <span className="text-[10px] text-gray-400 tabular-nums">
                  <span className="font-medium text-gray-300">{ind.symbol}</span>
                  {ind.price != null && <span> @ <span className="text-brand-gold font-mono">${Number(ind.price).toFixed(2)}</span></span>}
                  {ind.as_of && <span className="opacity-50 ml-1">{ind.as_of}</span>}
                </span>
              </div>

              {/* Indicator values */}
              {indEntries.length > 0 && (
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-[10px] min-w-[200px]">
                    <tbody className="divide-y divide-gray-800/50">
                      {indEntries.map(([key, val]) => {
                        // Columns that are boolean flags stored as 0/1
                        const isBoolFlag = ['uptrend', 'vol_ok'].includes(key);
                        const isNull = val === null || val === undefined;
                        return (
                        <tr key={key}>
                          <td className="py-0.5 text-gray-500 capitalize pr-3 whitespace-nowrap">
                            {key.replace(/_/g, ' ')}
                          </td>
                          <td className="py-0.5 text-right tabular-nums font-mono">
                            {isNull ? (
                              <span className="text-gray-600">—</span>
                            ) : isBoolFlag ? (
                              <span className={val === 1 || val === true ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
                                {val === 1 || val === true ? '✓ Yes' : '✗ No'}
                              </span>
                            ) : typeof val === 'number' ? (
                              <span className="text-gray-300">
                                {Math.abs(val) >= 1000 ? val.toFixed(2) : val.toFixed(4)}
                              </span>
                            ) : (
                              <span className="text-gray-300">{String(val)}</span>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Condition booleans */}
              {condEntries.length > 0 && (
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1 border-t border-gray-800/50">
                  {condEntries.map(([label, met]) => (
                    <div key={label} className="flex items-center gap-1 text-[10px]">
                      <span className={`font-bold ${met ? 'text-green-400' : 'text-red-400'}`}>
                        {met ? '✓' : '✗'}
                      </span>
                      <span className="text-gray-500 truncate capitalize">
                        {label.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Refresh */}
              <button
                onClick={() => onCheckIndicators && onCheckIndicators()}
                className="text-[10px] text-gray-600 hover:text-brand-gold transition-colors"
              >
                ↺ Refresh
              </button>
            </div>
          );
        })()}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
        <button
          onClick={onGoBacktest}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-brand-gold text-white hover:bg-brand-gold-dark font-medium flex-1 justify-center"
        >
          <MdTimeline size={14} /> Run Backtest
        </button>
        {/* Audit Excel download */}
        <button
          onClick={onDownloadAudit}
          disabled={auditLoading}
          title={`Download full audit Excel — all indicators, logic gates, trade-by-trade P&L${s.source_opt_run_id ? ` (OOS split from opt #${s.source_opt_run_id})` : ' (all data)'}`}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-brand-gold/60 hover:text-brand-gold transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {auditLoading
            ? <span className="inline-block w-3 h-3 border-2 border-gray-400/40 border-t-brand-gold rounded-full animate-spin" />
            : <MdFileDownload size={14} />}
          <span>Audit</span>
        </button>
        {s.is_active && (
          /* Active strategies run on the daily beat — no manual run.
             Manual controls are: re-size capital (queues incremental
             BUY/SELL OPG) or withdraw (liquidate + deactivate). */
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={beginEdit}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-brand-gold/50 text-brand-gold hover:bg-brand-gold/10 font-medium transition-colors"
              title="Change capital allocated to this strategy — queues the incremental buy/sell for the next open"
            >
              <MdEdit size={12} /> Change Alloc
            </button>
            {s.stop_requested_at ? (
              /* Wishlist #40: the model account's order sheet is out — the stop waits for fills / No fills. */
              <span className="flex items-center gap-1.5">
                <span
                  className="px-2 py-1 text-[11px] rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium"
                  title={`Requested ${new Date(s.stop_requested_at).toLocaleString()}. Applied when the account's order sheet is resolved (fills uploaded or No fills); its holdings are then sold on the next sheet.`}
                >
                  Stop requested
                </span>
                {onCancelStop && (
                  <button
                    onClick={onCancelStop}
                    className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold"
                    title="Keep the strategy running"
                  >
                    Cancel stop
                  </button>
                )}
              </span>
            ) : (
              <button
                onClick={onWithdraw}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-red-500/50 text-red-500 hover:bg-red-500/10 font-medium transition-colors"
                title="Sell all holdings (next open) and deactivate the strategy"
              >
                <MdStop size={12} /> Withdraw
              </button>
            )}
          </div>
        )}
      </div>
      {/* Pending order slices — surfaced here so the cancel decision
          happens in the Strategies tab, next to the strategy it affects. */}
      {pendingSlices.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Pending orders · next open</div>
          <div className="space-y-1.5">
            {pendingSlices.map((pend, pi) => {
              const sl = pend.slice;
              const buy = sl.intent_delta > 0;
              const kindLabel = ({
                entry: 'Entry', increase: 'Increase', reduce: 'Reduce',
                exit: 'Exit', withdraw: 'Withdraw', unknown: 'Order',
              })[sl.kind] || 'Order';
              return (
                /* Read-only visibility: orders are a derived consequence of
                   strategy state. To change them, use the strategy verbs
                   (Withdraw / Change Alloc) — not order-level cancellation. */
                <div key={`${pend.orderId}-${pi}`} className="flex items-center gap-2 text-xs min-w-0">
                  <span className="font-mono font-semibold">{pend.symbol}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${buy ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                    {kindLabel} {buy ? '+' : ''}{sl.intent_delta}
                  </span>
                  {pend.accountIsPaper === false && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-500">LIVE</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyCard;
